import { formatCurrency } from "@/utils/format";
import { ReportQuery } from "@/utils/period";
import {
  AdsPlatform,
  BreakdownParams,
  BreakdownResponse,
  BreakdownRow,
  BreakdownTotals,
  GoogleBreakdownRow,
  MetaBreakdownRow,
  Money,
} from "@/types/adsBreakdown";
import { pointCountForPeriod } from "./mockShared";

// Breakdown de anúncios fictício da conta Preview. Os dados são determinísticos
// (sem Math.random) para a paginação e a ordenação ficarem estáveis.

const money = (amount: number): Money => ({ amount, currency: "BRL", display: formatCurrency(amount) });
const moneyOrNull = (amount: number | null) => (amount == null ? null : money(amount));
const ratio = (a: number, b: number, factor = 1) => (b ? (a / b) * factor : null);

// [nome, índice do pai, status, objetivo/tipo, gasto em 30 dias, desempenho, vídeo]
type Entity = [string, number, string, string, number, number, boolean?];

const META_ACCOUNT = { id: "9876543210", name: "Loja Demo - Conta de Anúncios" };
const META: Record<"campaign" | "adset" | "ad", Entity[]> = {
  campaign: [
    ["[VENDAS] Coleção Verão — Advantage+", 0, "ACTIVE", "OUTCOME_SALES", 4820, 1],
    ["[VENDAS] Catálogo dinâmico — remarketing", 0, "ACTIVE", "OUTCOME_SALES", 3150, 1.4],
    ["[VENDAS] Black Friday — aquecimento", 0, "PAUSED", "OUTCOME_SALES", 2210, 0.5],
    ["[LEADS] Lista VIP lançamento", 0, "ACTIVE", "OUTCOME_LEADS", 1680, 0.6],
    ["[TRÁFEGO] Blog — conteúdos", 0, "ACTIVE", "OUTCOME_TRAFFIC", 920, 0.3],
    ["[VENDAS] Kits presente — público frio", 0, "WITH_ISSUES", "OUTCOME_SALES", 1340, 0.3],
    ["[ENGAJ.] Reels bastidores", 0, "ACTIVE", "OUTCOME_ENGAGEMENT", 610, 0.2],
    ["[VENDAS] Outlet — últimas peças", 0, "ACTIVE", "OUTCOME_SALES", 1980, 1.1],
  ],
  adset: [
    ["Advantage+ · Brasil 25–44", 0, "ACTIVE", "", 2980, 1],
    ["Advantage+ · Sudeste mulheres", 0, "ACTIVE", "", 1840, 0.9],
    ["Visitantes 30 dias", 1, "ACTIVE", "", 1920, 1.5],
    ["Carrinho abandonado 7 dias", 1, "ACTIVE", "", 1230, 1.7],
    ["Aquecimento · interesses moda", 2, "CAMPAIGN_PAUSED", "", 2210, 0.5],
    ["Formulário instantâneo · semelhantes", 3, "ACTIVE", "", 1680, 0.6],
    ["Leitores do blog · amplo", 4, "ACTIVE", "", 920, 0.3],
    ["Público frio · presentes", 5, "WITH_ISSUES", "", 1340, 0.3],
    ["Seguidores e semelhantes", 6, "ACTIVE", "", 610, 0.2],
    ["Outlet · compradores 180 dias", 7, "ACTIVE", "", 1980, 1.1],
  ],
  ad: [
    ["Reels 15s · Coleção Verão v2", 0, "ACTIVE", "", 1720, 1.1, true],
    ["Carrossel · looks da semana", 0, "ACTIVE", "", 1260, 0.9],
    ["Imagem · vestido de linho", 1, "ACTIVE", "", 1840, 0.9],
    ["Catálogo · produtos vistos", 2, "ACTIVE", "", 1920, 1.5],
    ["Catálogo · carrinho + cupom 10%", 3, "ACTIVE", "", 1230, 1.7],
    ["Vídeo 30s · contagem regressiva", 4, "CAMPAIGN_PAUSED", "", 2210, 0.5, true],
    ["Formulário · lista VIP", 5, "ACTIVE", "", 1680, 0.6],
    ["Kit presente · estático", 7, "WITH_ISSUES", "", 1220, 0.35],
    ["Kit presente · vídeo unboxing", 7, "DISAPPROVED", "", 120, 0.2, true],
    ["Reels · bastidores do ateliê", 8, "ACTIVE", "", 610, 0.2, true],
    ["Outlet · até 60% off", 9, "ACTIVE", "", 1980, 1.1],
  ],
};

const GOOGLE_CUSTOMERS = [
  { id: "1234567890", name: "Loja Demo - Conta Principal" },
  { id: "2345678901", name: "Loja Demo - Campanhas Sazonais" },
];
// Na campanha, o índice do pai é o customer; [estratégia, orçamento] ficam em GOOGLE_BIDDING.
const GOOGLE: Record<"campaign" | "adGroup" | "ad", Entity[]> = {
  campaign: [
    ["Pesquisa · Marca", 0, "ENABLED", "SEARCH", 1850, 1.6],
    ["Pesquisa · Categorias", 0, "ENABLED", "SEARCH", 2420, 0.9],
    ["PMax · Catálogo completo", 0, "ENABLED", "PERFORMANCE_MAX", 5120, 1.1],
    ["Shopping · Mais vendidos", 0, "ENABLED", "SHOPPING", 2980, 1.2],
    ["YouTube · Lançamento coleção", 0, "ENABLED", "VIDEO", 1120, 0.15],
    ["Display · Remarketing 30 dias", 0, "ENABLED", "DISPLAY", 640, 0.5],
    ["Pesquisa · Concorrentes", 0, "PAUSED", "SEARCH", 310, 0.4],
    ["Sazonal · Black Friday", 1, "ENABLED", "SEARCH", 1680, 0.8],
    ["PMax · Sazonal", 1, "ENABLED", "PERFORMANCE_MAX", 1460, 1],
  ],
  adGroup: [
    ["Marca · correspondência exata", 0, "ENABLED", "", 1240, 1.7],
    ["Marca · frase", 0, "ENABLED", "", 610, 1.4],
    ["Vestidos", 1, "ENABLED", "", 1380, 1],
    ["Conjuntos de linho", 1, "ENABLED", "", 1040, 0.8],
    ["Todos os produtos", 3, "ENABLED", "", 2980, 1.2],
    ["Lançamento · in-stream", 4, "ENABLED", "", 1120, 0.15],
    ["Visitantes 30 dias", 5, "ENABLED", "", 640, 0.5],
    ["Concorrentes · amplo", 6, "PAUSED", "", 310, 0.4],
    ["Black Friday · ofertas", 7, "ENABLED", "", 1680, 0.8],
  ],
  ad: [
    ["", 0, "ENABLED", "", 820, 1.8],
    ["", 0, "ENABLED", "", 420, 1.5],
    ["", 2, "ENABLED", "", 1380, 1],
    ["", 3, "PAUSED", "", 1040, 0.8],
    ["", 4, "ENABLED", "", 2980, 1.2],
    ["Lançamento coleção · 30s", 5, "ENABLED", "", 1120, 0.15],
    ["Remarketing · banner responsivo", 6, "ENABLED", "", 640, 0.5],
    ["", 7, "PAUSED", "", 310, 0.4],
    ["", 8, "ENABLED", "", 1680, 0.8],
  ],
};
const GOOGLE_BIDDING: [string, number][] = [
  ["TARGET_IMPRESSION_SHARE", 70], ["MAXIMIZE_CONVERSIONS", 90], ["TARGET_ROAS", 180], ["TARGET_ROAS", 100],
  ["TARGET_CPM", 40], ["MAXIMIZE_CONVERSIONS", 25], ["MANUAL_CPC", 20], ["MAXIMIZE_CONVERSIONS", 60], ["MAXIMIZE_CONVERSION_VALUE", 50],
];
const SALES_TAGS = ["OUTCOME_SALES", "SEARCH", "SHOPPING", "PERFORMANCE_MAX"];
const RANKINGS = ["ABOVE_AVERAGE", "AVERAGE", "BELOW_AVERAGE_35", "AVERAGE"];

// Números de entrega/conversão comuns às duas plataformas.
const baseMetrics = (spend: number, perf: number, tag: string, seed: number, search: boolean) => {
  const k = (seed % 11) / 10;
  const R = Math.round;
  const sales = SALES_TAGS.includes(tag);
  const impressions = R(spend * (search ? 22 + k * 20 : 60 + k * 70));
  const clicks = R(impressions * (search ? 0.05 + k * 0.03 : 0.009 + k * 0.012));
  const outboundClicks = R(clicks * 0.61);
  const landingPageViews = R(outboundClicks * 0.84);
  const visits = R(search ? clicks * 0.86 : landingPageViews * 0.91);
  const submits = R(visits * (tag === "OUTCOME_LEADS" ? 0.14 : 0.03));
  const orders = R(visits * (sales ? 0.022 : 0.006) * perf);
  const ticket = 148 + (seed % 5) * 26;
  return { k, sales, impressions, clicks, outboundClicks, landingPageViews, visits, submits, leads: R(submits * 0.82), orders, revenue: orders * ticket, ticket };
};

const yuhaIndexes = (spend: number, revenue: number, orders: number) => ({
  profit: money(revenue - spend),
  roas: ratio(revenue, spend),
  cpa: moneyOrNull(ratio(spend, orders)),
  averageTicket: moneyOrNull(ratio(revenue, orders)),
  profitMargin: revenue ? ((revenue - spend) / revenue) * 100 : null,
});

const metaRow = (level: "campaign" | "adset" | "ad", index: number, scale: number): MetaBreakdownRow => {
  const [name, parent, status, , spend30, perf, video] = META[level][index];
  const adset = level === "ad" ? META.adset[parent] : level === "adset" ? META.adset[index] : null;
  const campaignIndex = level === "campaign" ? index : adset![1];
  const campaign = META.campaign[campaignIndex];
  const tag = campaign[3];
  const seed = index * 37 + level.length * 13;
  const spend = Math.round(spend30 * scale * 100) / 100;
  const m = baseMetrics(spend, perf, tag, seed, false);
  const R = Math.round;
  const viewContent = R(m.landingPageViews * 0.68);
  const addToCart = R(viewContent * (m.sales ? 0.21 : 0.06));
  const initiateCheckout = R(addToCart * 0.56);
  const metaPurchases = R(initiateCheckout * (m.sales ? 0.46 : 0.12) * Math.min(1.4, perf + 0.3));
  const metaLeads = tag === "OUTCOME_LEADS" ? R(m.leads * 1.12) : R(m.leads * 0.2);
  const inlineLinkClicks = R(m.clicks * 0.74);
  const inlinePostEngagement = R(m.impressions * (tag === "OUTCOME_ENGAGEMENT" ? 0.06 : 0.012 + m.k * 0.01));
  const reach = R(m.impressions / (1.35 + m.k * 0.9));
  const results =
    { OUTCOME_SALES: metaPurchases, OUTCOME_LEADS: metaLeads, OUTCOME_TRAFFIC: inlineLinkClicks, OUTCOME_ENGAGEMENT: inlinePostEngagement }[tag] ?? 0;
  const plays = video ? R(m.impressions * 0.34) : 0;
  const watched = (share: number) => R(plays * share * (0.9 + m.k * 0.2));
  const hasBudget = (level === "campaign" && index % 2 === 0) || (level === "adset" && campaignIndex % 2 === 1);
  const id = `1202${4471003 + index * 7919 + level.length * 104729}0981`;

  return {
    id,
    name,
    level,
    adAccountId: META_ACCOUNT.id,
    adAccountName: META_ACCOUNT.name,
    campaignId: `1202${4471003 + campaignIndex * 7919 + 8 * 104729}0981`,
    campaignName: campaign[0],
    adsetId: adset ? `1202${4471003 + (level === "ad" ? parent : index) * 7919 + 5 * 104729}0981` : "",
    adsetName: adset ? adset[0] : "",
    attributionSetting: "7d_click_1d_view",
    currency: "BRL",
    spend: money(spend),
    impressions: m.impressions,
    clicks: m.clicks,
    inlineLinkClicks,
    socialSpend: money(R(spend * 0.04)),
    outboundClicks: m.outboundClicks,
    inlinePostEngagement,
    reach,
    frequency: m.impressions / reach,
    visits: m.visits,
    submits: m.submits,
    leads: m.leads,
    orders: m.orders,
    revenue: money(m.revenue),
    ctr: ratio(m.clicks, m.impressions, 100),
    cpc: moneyOrNull(ratio(spend, m.clicks)),
    cpm: moneyOrNull(ratio(spend, m.impressions, 1000)),
    ...yuhaIndexes(spend, m.revenue, m.orders),
    metaPurchases,
    metaRevenue: money(metaPurchases * m.ticket * 0.97),
    metaLeads,
    landingPageViews: m.landingPageViews,
    viewContent,
    addToCart,
    initiateCheckout,
    addPaymentInfo: R(initiateCheckout * 0.72),
    viewContentValue: money(viewContent * m.ticket),
    addToCartValue: money(addToCart * m.ticket),
    initiateCheckoutValue: money(initiateCheckout * m.ticket),
    results,
    resultType: results ? `actions:${tag}` : undefined,
    costPerResult: moneyOrNull(ratio(spend, results)),
    videoPlays: plays,
    video25PercentWatched: watched(0.55),
    video50PercentWatched: watched(0.34),
    video75PercentWatched: watched(0.22),
    video95PercentWatched: watched(0.14),
    video100PercentWatched: watched(0.11),
    video30SecWatched: watched(0.16),
    videoContinuous2SecWatched: watched(0.62),
    videoAvgTimeWatchedMs: video ? R((4.2 + m.k * 5) * 1000) : null,
    status: status.endsWith("_PAUSED") ? "ACTIVE" : status,
    effectiveStatus: status,
    objective: level === "campaign" ? tag : "",
    buyingType: level === "campaign" ? "AUCTION" : "",
    bidStrategy: level === "campaign" ? ["LOWEST_COST_WITHOUT_CAP", "COST_CAP", "LOWEST_COST_WITH_MIN_ROAS"][index % 3] : "",
    dailyBudget: hasBudget ? money(Math.max(20, R(spend30 / 300) * 10 + 10)) : null,
    lifetimeBudget: null,
    budgetRemaining: null,
    startTime: `2026-0${8 + (index % 2)}-${String(1 + ((index * 7) % 27)).padStart(2, "0")}T00:00:00-0300`,
    stopTime: index % 4 === 3 ? "2026-11-30T23:59:00-0300" : "",
    createdTime: "2026-07-28T10:12:00-0300",
    updatedTime: "2026-10-30T16:45:00-0300",
    issuesCount: status === "WITH_ISSUES" ? 2 : status === "DISAPPROVED" ? 1 : 0,
    qualityRanking: level === "ad" ? RANKINGS[index % 2] : "",
    engagementRateRanking: level === "ad" ? RANKINGS[(index + 1) % 3] : "",
    conversionRateRanking: level === "ad" ? RANKINGS[(index + 2) % 4] : "",
  };
};

const googleRow = (level: "campaign" | "adGroup" | "ad", index: number, scale: number): GoogleBreakdownRow => {
  const [name, parent, status, , spend30, perf] = GOOGLE[level][index];
  const group = level === "ad" ? GOOGLE.adGroup[parent] : level === "adGroup" ? GOOGLE.adGroup[index] : null;
  const campaignIndex = level === "campaign" ? index : group![1];
  const campaign = GOOGLE.campaign[campaignIndex];
  const customer = GOOGLE_CUSTOMERS[campaign[1]];
  const tag = campaign[3];
  const seed = index * 37 + level.length * 13;
  const spend = Math.round(spend30 * scale * 100) / 100;
  const m = baseMetrics(spend, perf, tag, seed, tag === "SEARCH");
  const R = Math.round;
  const conversions = R((m.orders * 1.17 + m.leads * 0.3) * 10) / 10;
  const videoViews = tag === "VIDEO" ? R(m.impressions * 0.31) : 0;
  const hasReach = level === "campaign" && (tag === "VIDEO" || tag === "DISPLAY");
  const reach = hasReach ? R(m.impressions / (1.4 + m.k)) : null;

  return {
    id: String(18800000000 + index * 7919 + level.length * 104729),
    name,
    level,
    customerId: customer.id,
    customerName: customer.name,
    campaignId: String(18800000000 + campaignIndex * 7919 + 8 * 104729),
    campaignName: campaign[0],
    adGroupId: group ? String(15500000000 + (level === "ad" ? parent : index) * 7919) : "",
    adGroupName: group ? group[0] : "",
    status,
    channelType: tag,
    biddingStrategy: level === "campaign" ? GOOGLE_BIDDING[index][0] : "",
    budget: level === "campaign" ? money(GOOGLE_BIDDING[index][1]) : null,
    currency: "BRL",
    spend: money(spend),
    impressions: m.impressions,
    clicks: m.clicks,
    interactions: m.clicks + videoViews,
    videoViews,
    reach,
    frequency: reach ? m.impressions / reach : null,
    visits: m.visits,
    submits: m.submits,
    leads: m.leads,
    orders: m.orders,
    revenue: money(m.revenue),
    ctr: ratio(m.clicks, m.impressions, 100),
    cpc: moneyOrNull(ratio(spend, m.clicks)),
    cpm: moneyOrNull(ratio(spend, m.impressions, 1000)),
    ...yuhaIndexes(spend, m.revenue, m.orders),
    conversions,
    conversionsValue: money(m.revenue * 1.06),
    allConversions: R(conversions * 13.8) / 10,
    allConversionsValue: money(m.revenue * 1.19),
    viewThroughConversions: R(conversions * 0.08),
    crossDeviceConversions: R(conversions * 1.3) / 10,
  };
};

// Soma as linhas de um customer/conta para os níveis account/customer
// (sem métricas do Yuha nesses níveis, como na API).
const accountRow = <T extends BreakdownRow>(rows: T[], base: Partial<T>): T => {
  const sum = (key: keyof T) => rows.reduce((acc, row) => acc + ((row[key] as unknown as number) || 0), 0);
  const sumMoney = (key: keyof T) => money(rows.reduce((acc, row) => acc + ((row[key] as unknown as Money)?.amount || 0), 0));
  const out: Record<string, unknown> = { ...rows[0] };
  Object.keys(rows[0]).forEach((key) => {
    const value = rows[0][key as keyof T] as unknown;
    if (typeof value === "number") out[key] = sum(key as keyof T);
    else if (value && typeof value === "object" && "amount" in (value as Money)) out[key] = sumMoney(key as keyof T);
  });
  const spend = (out.spend as Money).amount;
  Object.assign(out, base, {
    campaignId: "", campaignName: "", status: "",
    visits: 0, submits: 0, leads: 0, orders: 0, revenue: money(0),
    profit: null, roas: null, cpa: null, averageTicket: null, profitMargin: null,
    ctr: ratio(out.clicks as number, out.impressions as number, 100),
    cpc: moneyOrNull(ratio(spend, out.clicks as number)),
    cpm: moneyOrNull(ratio(spend, out.impressions as number, 1000)),
  });
  return out as unknown as T;
};

const allRows = (platform: AdsPlatform, level: string, scale: number): BreakdownRow[] => {
  if (platform === "meta") {
    if (level === "account") {
      const campaigns = META.campaign.map((_, i) => metaRow("campaign", i, scale));
      const row = accountRow<MetaBreakdownRow>(campaigns, {
        id: META_ACCOUNT.id, name: META_ACCOUNT.id, level: "account", objective: "", effectiveStatus: "", buyingType: "", bidStrategy: "",
        dailyBudget: null, adsetId: "", adsetName: "", costPerResult: null, resultType: undefined, videoAvgTimeWatchedMs: null, issuesCount: 0, reach: Math.round(campaigns.reduce((a, r) => a + (r.reach ?? 0), 0) * 0.78),
      });
      row.frequency = row.reach ? row.impressions / row.reach : null;
      return [row];
    }
    const key = level as keyof typeof META;
    return META[key].map((_, i) => metaRow(key, i, scale));
  }
  if (level === "customer") {
    const campaigns = GOOGLE.campaign.map((_, i) => googleRow("campaign", i, scale));
    return GOOGLE_CUSTOMERS.map((customer) =>
      accountRow<GoogleBreakdownRow>(campaigns.filter((row) => row.customerId === customer.id), {
        id: customer.id, name: customer.name, level: "customer", channelType: "", biddingStrategy: "", budget: null,
        adGroupId: "", adGroupName: "", reach: null, frequency: null,
      }),
    );
  }
  const key = level as keyof typeof GOOGLE;
  // Performance Max não tem grupos nem anúncios.
  return GOOGLE[key].map((_, i) => googleRow(key, i, scale));
};

const sortValue = (row: BreakdownRow, key: string): number | string | null => {
  const value = (row as unknown as Record<string, unknown>)[key];
  if (value == null) return null;
  if (typeof value === "object") return (value as Money).amount;
  return value as number | string;
};

const NON_SUMMABLE = new Set(["reach", "frequency"]);

const buildTotals = (platform: AdsPlatform, rows: BreakdownRow[], level: string, filtered: boolean): BreakdownTotals => {
  const totals: BreakdownTotals = {};
  if (rows.length === 0) return totals;
  Object.keys(rows[0]).forEach((key) => {
    const sample = (rows[0] as unknown as Record<string, unknown>)[key];
    if (NON_SUMMABLE.has(key)) return;
    if (typeof sample === "number") totals[key] = rows.reduce((a, r) => a + ((sortValue(r, key) as number) || 0), 0);
    else if (sample && typeof sample === "object" && "amount" in (sample as Money))
      totals[key] = money(rows.reduce((a, r) => a + ((sortValue(r, key) as number) || 0), 0));
  });
  const n = (key: string) => (totals[key] as number) || 0;
  const m = (key: string) => (totals[key] as Money | undefined)?.amount ?? 0;
  const spend = m("spend");
  const noYuha = level === "account" || level === "customer";
  Object.assign(totals, {
    ctr: ratio(n("clicks"), n("impressions"), 100),
    cpc: moneyOrNull(ratio(spend, n("clicks"))),
    cpm: moneyOrNull(ratio(spend, n("impressions"), 1000)),
    profit: noYuha ? null : money(m("revenue") - spend),
    roas: noYuha ? null : ratio(m("revenue"), spend),
    cpa: noYuha ? null : moneyOrNull(ratio(spend, n("orders"))),
    averageTicket: noYuha ? null : moneyOrNull(ratio(m("revenue"), n("orders"))),
    profitMargin: noYuha || !m("revenue") ? null : ((m("revenue") - spend) / m("revenue")) * 100,
  });
  if (platform === "meta") {
    const types = new Set(rows.map((r) => (r as MetaBreakdownRow).resultType ?? ""));
    totals.resultsUnavailable = types.size > 1;
    if (totals.resultsUnavailable) totals.results = 0;
    totals.costPerResult = totals.resultsUnavailable ? null : moneyOrNull(ratio(spend, n("results")));
    totals.reach = filtered ? null : Math.round(rows.reduce((a, r) => a + (r.reach ?? 0), 0) * 0.78);
    totals.frequency = totals.reach ? n("impressions") / (totals.reach as number) : null;
    if (filtered) totals.nonAdditiveReason = "filtered";
  } else {
    totals.reach = null;
    totals.frequency = null;
    totals.nonAdditiveReason = "not_aggregatable";
  }
  return totals;
};

export const mockAdsBreakdown = (platform: AdsPlatform, query: ReportQuery, params: BreakdownParams): BreakdownResponse => {
  const scale = pointCountForPeriod(query.period, query.startDate, query.endDate) / 30;
  let rows = allRows(platform, params.level, scale);

  const search = params.search?.trim().toLowerCase();
  const filtered = !!(search || params.status?.length || params.objective?.length || params.channelType?.length || Object.keys(params.ranges ?? {}).length);
  rows = rows.filter((row) => {
    const r = row as unknown as Record<string, unknown>;
    if (search && !row.name.toLowerCase().includes(search)) return false;
    if (params.status?.length && !params.status.includes((platform === "meta" ? r.effectiveStatus : r.status) as string)) return false;
    if (params.objective?.length && !params.objective.includes(r.objective as string)) return false;
    if (params.channelType?.length && !params.channelType.includes(r.channelType as string)) return false;
    return Object.entries(params.ranges ?? {}).every(([key, limit]) => {
      const value = sortValue(row, key.replace(/(Min|Max)$/, ""));
      if (value == null) return false;
      return key.endsWith("Min") ? (value as number) >= limit : (value as number) <= limit;
    });
  });

  const dir = params.sortDir === "asc" ? 1 : -1;
  rows.sort((a, b) => {
    const va = sortValue(a, params.sortBy);
    const vb = sortValue(b, params.sortBy);
    if (va == null && vb == null) return 0;
    if (va == null) return 1;
    if (vb == null) return -1;
    if (typeof va === "string") return va.localeCompare(String(vb)) * dir;
    return ((va as number) - (vb as number)) * dir;
  });

  const totalPages = Math.max(1, Math.ceil(rows.length / params.limit));
  const page = rows.slice((params.page - 1) * params.limit, params.page * params.limit);
  const noYuha = params.level === "account" || params.level === "customer";
  const pmax = GOOGLE.campaign.filter((c) => c[3] === "PERFORMANCE_MAX");

  return {
    data: {
      level: params.level,
      currency: "BRL",
      start: query.startDate.toISOString(),
      end: query.endDate.toISOString(),
      rowCount: rows.length,
      truncated: false,
      rows: page,
      totals: buildTotals(platform, rows, params.level, filtered),
      mixedCurrency: false,
      fxRate: null,
      fxAsOf: null,
      ...(noYuha ? { yuhaMetricsReason: `unavailable_at_${params.level}_level` } : {}),
      ...(platform === "meta"
        ? { warnings: { restatementWindow: query.endDate.getTime() > Date.now() - 28 * 86400000 } }
        : {
            attribution: {
              conversionActions: [
                { name: "Compra no site", category: "PURCHASE", attributionModel: "GOOGLE_SEARCH_ATTRIBUTION_DATA_DRIVEN", clickThroughLookbackDays: 30, viewThroughLookbackDays: 1, countingType: "MANY_PER_CLICK", includedInConversionsMetric: true },
                { name: "Lead · formulário de contato", category: "LEAD", attributionModel: "GOOGLE_ADS_LAST_CLICK", clickThroughLookbackDays: 30, viewThroughLookbackDays: 1, countingType: "ONE_PER_CLICK", includedInConversionsMetric: true },
                { name: "Inscrição na newsletter", category: "SIGNUP", attributionModel: "GOOGLE_ADS_LAST_CLICK", clickThroughLookbackDays: 7, viewThroughLookbackDays: 1, countingType: "ONE_PER_CLICK", includedInConversionsMetric: false },
                { name: "Adição ao carrinho", category: "ADD_TO_CART", attributionModel: "GOOGLE_SEARCH_ATTRIBUTION_DATA_DRIVEN", clickThroughLookbackDays: 30, viewThroughLookbackDays: 1, countingType: "MANY_PER_CLICK", includedInConversionsMetric: false },
              ],
            },
            ...(params.level === "adGroup" || params.level === "ad"
              ? { excludedCampaigns: pmax.map((c, i) => ({ id: String(18800000000 + i), name: c[0], spendMicros: Math.round(c[4] * scale * 1e6) })) }
              : {}),
          }),
    },
    pagination: { page: params.page, total: totalPages },
  };
};
